/**
 * 🎨 COUNTDOWN BAR FLOATING TOOLBAR 2025
 * 
 * Floating Toolbar für Countdown Bar Element
 * Inspiriert von ButtonFloatingToolbar
 * 
 * Sections:
 * - Bar Style (Height, Radius, Padding, Background, Border, Shadow)
 * - Text Style (Font Size, Weight, Color, Font Family)
 * - Countdown Style (Font Size, Weight, Color, Font Family)
 * - Timer Settings (Mode, Date, Display Format)
 * - Spacing (Margins)
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useFont } from '../../Design/FontContext';
import { titleFonts, textFonts } from '../../utils/fontsHelper';
import './CountdownBarFloatingToolbar.css';

function CountdownBarFloatingToolbar({
    element,
    onClose,
    onStyleChange,
    colors = {},
    fonts: brandFonts = {},
    anchorRef,
    isMobileLayout = false,
    isEditMode,
    isSelected
}) {
    const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
    const toolbarRef = useRef(null);
    const fonts = useFont();
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // 🎯 Expandable Sections State
    const [expandedSection, setExpandedSection] = useState(null);
    
    // 🎯 Device Toggle States
    const [barDevice, setBarDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    const [textDevice, setTextDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    const [countdownDevice, setCountdownDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    const [spacingDevice, setSpacingDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // Sync device toggles with isMobileLayout
    useEffect(() => {
        setBarDevice(isMobileLayout ? 'mobile' : 'desktop');
        setTextDevice(isMobileLayout ? 'mobile' : 'desktop');
        setCountdownDevice(isMobileLayout ? 'mobile' : 'desktop');
        setSpacingDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);
    
    // 🎯 Drag Handlers
    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 400);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 300);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true);
    };
    
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging]);
    
    // Update Toolbar Position
    useEffect(() => {
        if (isEditMode && isSelected && !isManuallyPositioned && !expandedSection) {
            const top = 80;
            const left = window.innerWidth / 2;
            setToolbarPosition({ top, left });
        }
    }, [isEditMode, isSelected, isManuallyPositioned, expandedSection]);
    
    // Handle Outside Click
    useEffect(() => {
        const handleClickOutside = (event) => {
            // ✅ Check via closest() - funktioniert auch nach Re-Renders!
            const clickedInToolbar = event.target.closest('.countdown-bar-floating-toolbar-portal');
            const clickedInAnchor = anchorRef && anchorRef.current && anchorRef.current.contains(event.target);
            
            // Close only if clicked OUTSIDE of toolbar AND anchor
            if (!clickedInToolbar && !clickedInAnchor) {
                setExpandedSection(null);
                if (onClose) onClose();
            }
        };
        
        // ✅ Use mousedown to catch event BEFORE React's onClick
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, anchorRef]);
    
    if (!isEditMode || !isSelected) return null;

    return ReactDOM.createPortal(
        <div 
            ref={toolbarRef}
            className={`countdown-bar-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
        >
            <div 
                className="countdown-bar-toolbar"
                onMouseDown={(e) => {
                    // ✅ FIX: Stoppe alle mousedown Events innerhalb der Toolbar
                    // Verhindert, dass der Outside-Click-Handler triggert
                    e.stopPropagation();
                }}
            >
                {/* 🎨 MAIN TOOLBAR BUTTONS */}
                <div className="countdown-bar-toolbar-row">
                    {/* Drag Handle */}
                    <div 
                        className="countdown-bar-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    {/* 1. Bar Style */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'barStyle' ? null : 'barStyle');
                        }}
                        className={`countdown-bar-toolbar-btn ${expandedSection === 'barStyle' ? 'is-active' : ''}`}
                        title="Bar Style (Size, Background, Border, Shadow)"
                    >
                        <i className="fas fa-sliders-h"></i>
                    </button>

                    {/* 2. Text Style */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'textStyle' ? null : 'textStyle');
                        }}
                        className={`countdown-bar-toolbar-btn ${expandedSection === 'textStyle' ? 'is-active' : ''}`}
                        title="Text Style"
                    >
                        <i className="fas fa-font"></i>
                    </button>

                    {/* 3. Countdown Style */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'countdownStyle' ? null : 'countdownStyle');
                        }}
                        className={`countdown-bar-toolbar-btn ${expandedSection === 'countdownStyle' ? 'is-active' : ''}`}
                        title="Countdown Style"
                    >
                        <i className="fas fa-clock"></i>
                    </button>
                    
                    {/* 4. Timer Container Style */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'timerContainer' ? null : 'timerContainer');
                        }}
                        className={`countdown-bar-toolbar-btn ${expandedSection === 'timerContainer' ? 'is-active' : ''} ${(element.timerContainerBorderEnabled || element.timerContainerShadowEnabled || element.timerContainerPadding > 0) ? 'has-feature' : ''}`}
                        title="Timer Container (Position, Border, Background)"
                    >
                        <i className="fas fa-box"></i>
                    </button>
                    
                    {/* Separator */}
                    <div className="countdown-bar-toolbar-separator"></div>

                    {/* 5. Timer Settings */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'timerSettings' ? null : 'timerSettings');
                        }}
                        className={`countdown-bar-toolbar-btn ${expandedSection === 'timerSettings' ? 'is-active' : ''}`}
                        title="Timer Settings (Mode, Date, Format)"
                    >
                        <i className="fas fa-cog"></i>
                    </button>

                    {/* 6. Spacing */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'spacing' ? null : 'spacing');
                        }}
                        className={`countdown-bar-toolbar-btn ${expandedSection === 'spacing' ? 'is-active' : ''}`}
                        title="Spacing (Margins)"
                    >
                        <i className="fas fa-arrows-alt-v"></i>
                    </button>
                </div>
                
                {/* 🎨 EXPANDABLE SECTIONS */}
                
                {/* 1️⃣ BAR STYLE SECTION */}
                {expandedSection === 'barStyle' && (
                    <div 
                        className="countdown-bar-expanded-section"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Desktop/Mobile Toggle */}
                        <div className="cdb-device-row">
                            <span className="cdb-device-label">Desktop</span>
                            <div 
                                className={`cdb-toggle ${barDevice === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setBarDevice(barDevice === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="cdb-toggle-slider"></div>
                            </div>
                            <span className="cdb-device-label">Mobile</span>
                        </div>
                        
                        {/* Height */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Height</span>
                            <input
                                type="range"
                                min="50"
                                max="200"
                                value={barDevice === 'desktop' 
                                    ? (element.barHeight ?? 100) 
                                    : (element.barHeightMobile ?? 80)}
                                onChange={(e) => {
                                    const newHeight = parseInt(e.target.value);
                                    if (barDevice === 'desktop') {
                                        onStyleChange({ barHeight: newHeight });
                                    } else {
                                        onStyleChange({ barHeightMobile: newHeight });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {barDevice === 'desktop' 
                                    ? (element.barHeight ?? 100) 
                                    : (element.barHeightMobile ?? 80)}px
                            </span>
                        </div>
                        
                        {/* Border Radius */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Radius</span>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                value={barDevice === 'desktop' 
                                    ? (element.barBorderRadius ?? 12) 
                                    : (element.barBorderRadiusMobile ?? 12)}
                                onChange={(e) => {
                                    const newRadius = parseInt(e.target.value);
                                    if (barDevice === 'desktop') {
                                        onStyleChange({ barBorderRadius: newRadius });
                                    } else {
                                        onStyleChange({ barBorderRadiusMobile: newRadius });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {barDevice === 'desktop' 
                                    ? (element.barBorderRadius ?? 12) 
                                    : (element.barBorderRadiusMobile ?? 12)}px
                            </span>
                        </div>
                        
                        {/* Padding */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Padding</span>
                            <input
                                type="range"
                                min="10"
                                max="60"
                                value={barDevice === 'desktop' 
                                    ? (element.barPadding ?? 20) 
                                    : (element.barPaddingMobile ?? 20)}
                                onChange={(e) => {
                                    const newPadding = parseInt(e.target.value);
                                    if (barDevice === 'desktop') {
                                        onStyleChange({ barPadding: newPadding });
                                    } else {
                                        onStyleChange({ barPaddingMobile: newPadding });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {barDevice === 'desktop' 
                                    ? (element.barPadding ?? 20) 
                                    : (element.barPaddingMobile ?? 20)}px
                            </span>
                        </div>
                        
                        {/* Background */}
                        <div className="cdb-color-group">
                            <div className="cdb-effect-header">
                                <span className="cdb-color-label">Background</span>
                                <label className="cdb-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={element.barGradientEnabled || false}
                                        onChange={(e) => {
                                            onStyleChange({ barGradientEnabled: e.target.checked });
                                        }}
                                        className="cdb-custom-toggle-input"
                                    />
                                    <span className="cdb-custom-toggle-slider"></span>
                                    <span className="cdb-custom-toggle-label">Gradient</span>
                                </label>
                            </div>
                            
                            {!element.barGradientEnabled && (
                                <div className="cdb-brand-colors">
                                    {Object.values(colors).map((brandColor, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className="cdb-brand-color"
                                            style={{ backgroundColor: brandColor }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onStyleChange({ barBackgroundColor: brandColor });
                                            }}
                                            title={`Background Color ${index + 1}`}
                                        />
                                    ))}
                                    {/* Transparent Option */}
                                    <button
                                        type="button"
                                        className="cdb-brand-color"
                                        style={{ 
                                            background: 'linear-gradient(135deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%), linear-gradient(135deg, #f0f0f0 25%, white 25%, white 75%, #f0f0f0 75%)',
                                            backgroundSize: '8px 8px',
                                            backgroundPosition: '0 0, 4px 4px'
                                        }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ barBackgroundColor: 'transparent' });
                                        }}
                                        title="Transparent"
                                    />
                                </div>
                            )}
                            
                            {element.barGradientEnabled && (
                                <div className="cdb-gradient-controls">
                                    <div className="cdb-gradient-row">
                                        <span className="cdb-gradient-label">Color 1</span>
                                        <div className="cdb-brand-colors cdb-brand-colors-small">
                                            {Object.values(colors).map((brandColor, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className="cdb-brand-color cdb-brand-color-small"
                                                    style={{ backgroundColor: brandColor }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onStyleChange({ barGradientColor1: brandColor });
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    <div className="cdb-gradient-row">
                                        <span className="cdb-gradient-label">Color 2</span>
                                        <div className="cdb-brand-colors cdb-brand-colors-small">
                                            {Object.values(colors).map((brandColor, index) => (
                                                <button
                                                    key={index}
                                                    type="button"
                                                    className="cdb-brand-color cdb-brand-color-small"
                                                    style={{ backgroundColor: brandColor }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onStyleChange({ barGradientColor2: brandColor });
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Border */}
                        <div className="cdb-effect-group">
                            <div className="cdb-effect-header">
                                <label className="cdb-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={element.barBorderEnabled || false}
                                        onChange={(e) => onStyleChange({ barBorderEnabled: e.target.checked })}
                                        className="cdb-custom-toggle-input"
                                    />
                                    <span className="cdb-custom-toggle-slider"></span>
                                    <span className="cdb-custom-toggle-label">Border</span>
                                </label>
                            </div>
                            
                            {element.barBorderEnabled && (
                                <div className="cdb-effect-controls">
                                    <div className="cdb-slider-control">
                                        <span className="cdb-slider-label">Width</span>
                                        <input
                                            type="range"
                                            min="1"
                                            max="8"
                                            value={element.barBorderWidth ?? 2}
                                            onChange={(e) => onStyleChange({ barBorderWidth: parseInt(e.target.value) })}
                                            className="cdb-slider"
                                        />
                                        <span className="cdb-slider-value">{element.barBorderWidth ?? 2}px</span>
                                    </div>
                                    <div className="cdb-brand-colors cdb-brand-colors-small">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="cdb-brand-color cdb-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ barBorderColor: brandColor });
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Shadow */}
                        <div className="cdb-effect-group">
                            <div className="cdb-effect-header">
                                <label className="cdb-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={element.barShadowEnabled || false}
                                        onChange={(e) => onStyleChange({ barShadowEnabled: e.target.checked })}
                                        className="cdb-custom-toggle-input"
                                    />
                                    <span className="cdb-custom-toggle-slider"></span>
                                    <span className="cdb-custom-toggle-label">Shadow</span>
                                </label>
                            </div>
                            
                            {element.barShadowEnabled && (
                                <div className="cdb-effect-controls">
                                    <div className="cdb-slider-control">
                                        <span className="cdb-slider-label">Blur</span>
                                        <input
                                            type="range"
                                            min="5"
                                            max="50"
                                            value={element.shadowIntensity ?? 20}
                                            onChange={(e) => onStyleChange({ shadowIntensity: parseInt(e.target.value) })}
                                            className="cdb-slider"
                                        />
                                        <span className="cdb-slider-value">{element.shadowIntensity ?? 20}px</span>
                                    </div>
                                    <div className="cdb-slider-control">
                                        <span className="cdb-slider-label">Distance</span>
                                        <input
                                            type="range"
                                            min="0"
                                            max="30"
                                            value={element.shadowDistance ?? 8}
                                            onChange={(e) => onStyleChange({ shadowDistance: parseInt(e.target.value) })}
                                            className="cdb-slider"
                                        />
                                        <span className="cdb-slider-value">{element.shadowDistance ?? 8}px</span>
                                    </div>
                                    <div className="cdb-slider-control">
                                        <span className="cdb-slider-label">Opacity</span>
                                        <input
                                            type="range"
                                            min="5"
                                            max="80"
                                            value={element.shadowOpacity ?? 20}
                                            onChange={(e) => onStyleChange({ shadowOpacity: parseInt(e.target.value) })}
                                            className="cdb-slider"
                                        />
                                        <span className="cdb-slider-value">{element.shadowOpacity ?? 20}%</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <span className="cdb-section-label">Bar Style</span>
                    </div>
                )}

                {/* 2️⃣ TEXT STYLE SECTION */}
                {expandedSection === 'textStyle' && (
                    <div 
                        className="countdown-bar-expanded-section"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* View Mode Info */}
                        <div className="cdb-view-mode-info">
                            <i className={`fas ${isMobileLayout ? 'fa-mobile-alt' : 'fa-desktop'}`}></i>
                            <span>Editing: {isMobileLayout ? 'Mobile' : 'Desktop'} View</span>
                        </div>
                        
                        {/* Font Size */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Size</span>
                            <input
                                type="range"
                                min="12"
                                max="100"
                                value={isMobileLayout 
                                    ? (element.textFontSizeMobile ?? element.textFontSize ?? 16) 
                                    : (element.textFontSize ?? 18)}
                                onChange={(e) => {
                                    const newSize = parseInt(e.target.value);
                                    console.log('📏 [Toolbar] Text Font Size changed:', {
                                        isMobileLayout,
                                        newSize,
                                        property: isMobileLayout ? 'textFontSizeMobile' : 'textFontSize'
                                    });
                                    if (isMobileLayout) {
                                        onStyleChange({ textFontSizeMobile: newSize });
                                    } else {
                                        onStyleChange({ textFontSize: newSize });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {isMobileLayout 
                                    ? (element.textFontSizeMobile ?? element.textFontSize ?? 16) 
                                    : (element.textFontSize ?? 18)}px
                            </span>
                        </div>
                        
                        {/* Font Weight */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Weight</span>
                            <input
                                type="range"
                                min="300"
                                max="900"
                                step="100"
                                value={isMobileLayout 
                                    ? (element.textFontWeightMobile ?? element.textFontWeight ?? '500') 
                                    : (element.textFontWeight ?? '500')}
                                onChange={(e) => {
                                    const newWeight = e.target.value;
                                    if (isMobileLayout) {
                                        onStyleChange({ textFontWeightMobile: newWeight });
                                    } else {
                                        onStyleChange({ textFontWeight: newWeight });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {isMobileLayout 
                                    ? (element.textFontWeightMobile ?? element.textFontWeight ?? '500') 
                                    : (element.textFontWeight ?? '500')}
                            </span>
                        </div>
                        
                        {/* Text Color */}
                        <div className="cdb-color-group">
                            <span className="cdb-color-label">Text Color</span>
                            <div className="cdb-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="cdb-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ textColor: brandColor });
                                        }}
                                        title={`Text Color ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                        
                        {/* Font Family */}
                        <div className="cdb-font-family-section">
                            <span className="cdb-color-label">Font Family</span>
                            <div className="cdb-font-family-list">
                                {/* Brand Fonts */}
                                {brandFonts?.globalSmallFont && (
                                    <button
                                        type="button"
                                        className={`cdb-font-family-option brand-font ${element.textFontFamily === brandFonts.globalSmallFont ? 'is-active' : ''}`}
                                        style={{ fontFamily: brandFonts.globalSmallFont }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ textFontFamily: brandFonts.globalSmallFont });
                                        }}
                                    >
                                        ⭐ {brandFonts.globalSmallFont}
                                    </button>
                                )}
                                {brandFonts?.globalBigFont && brandFonts.globalBigFont !== brandFonts.globalSmallFont && (
                                    <button
                                        type="button"
                                        className={`cdb-font-family-option brand-font ${element.textFontFamily === brandFonts.globalBigFont ? 'is-active' : ''}`}
                                        style={{ fontFamily: brandFonts.globalBigFont }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ textFontFamily: brandFonts.globalBigFont });
                                        }}
                                    >
                                        ⭐ {brandFonts.globalBigFont}
                                    </button>
                                )}
                                
                                {/* System Fonts (first 5) */}
                                {textFonts.slice(0, 5).map((font, index) => (
                                    <button
                                        key={`text-${index}`}
                                        type="button"
                                        className={`cdb-font-family-option ${element.textFontFamily === font ? 'is-active' : ''}`}
                                        style={{ fontFamily: font }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ textFontFamily: font });
                                        }}
                                    >
                                        {font}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        <span className="cdb-section-label">Text Style</span>
                    </div>
                )}

                {/* 3️⃣ COUNTDOWN STYLE SECTION */}
                {expandedSection === 'countdownStyle' && (
                    <div 
                        className="countdown-bar-expanded-section"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* View Mode Info */}
                        <div className="cdb-view-mode-info">
                            <i className={`fas ${isMobileLayout ? 'fa-mobile-alt' : 'fa-desktop'}`}></i>
                            <span>Editing: {isMobileLayout ? 'Mobile' : 'Desktop'} View</span>
                        </div>
                        
                        {/* Font Size */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Size</span>
                            <input
                                type="range"
                                min="18"
                                max="100"
                                value={isMobileLayout 
                                    ? (element.countdownFontSizeMobile ?? element.countdownFontSize ?? 28) 
                                    : (element.countdownFontSize ?? 32)}
                                onChange={(e) => {
                                    const newSize = parseInt(e.target.value);
                                    console.log('📏 [Toolbar] Countdown Font Size changed:', {
                                        isMobileLayout,
                                        newSize,
                                        property: isMobileLayout ? 'countdownFontSizeMobile' : 'countdownFontSize'
                                    });
                                    if (isMobileLayout) {
                                        onStyleChange({ countdownFontSizeMobile: newSize });
                                    } else {
                                        onStyleChange({ countdownFontSize: newSize });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {isMobileLayout 
                                    ? (element.countdownFontSizeMobile ?? element.countdownFontSize ?? 28) 
                                    : (element.countdownFontSize ?? 32)}px
                            </span>
                        </div>
                        
                        {/* Font Weight */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Weight</span>
                            <input
                                type="range"
                                min="300"
                                max="900"
                                step="100"
                                value={isMobileLayout 
                                    ? (element.countdownFontWeightMobile ?? element.countdownFontWeight ?? '700') 
                                    : (element.countdownFontWeight ?? '700')}
                                onChange={(e) => {
                                    const newWeight = e.target.value;
                                    if (isMobileLayout) {
                                        onStyleChange({ countdownFontWeightMobile: newWeight });
                                    } else {
                                        onStyleChange({ countdownFontWeight: newWeight });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {isMobileLayout 
                                    ? (element.countdownFontWeightMobile ?? element.countdownFontWeight ?? '700') 
                                    : (element.countdownFontWeight ?? '700')}
                            </span>
                        </div>
                        
                        {/* Countdown Color */}
                        <div className="cdb-color-group">
                            <span className="cdb-color-label">Countdown Color</span>
                            <div className="cdb-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="cdb-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ countdownColor: brandColor });
                                        }}
                                        title={`Countdown Color ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                        
                        {/* Font Family */}
                        <div className="cdb-font-family-section">
                            <span className="cdb-color-label">Font Family</span>
                            <div className="cdb-font-family-list">
                                {/* Brand Fonts */}
                                {brandFonts?.globalBigFont && (
                                    <button
                                        type="button"
                                        className={`cdb-font-family-option brand-font ${element.countdownFontFamily === brandFonts.globalBigFont ? 'is-active' : ''}`}
                                        style={{ fontFamily: brandFonts.globalBigFont }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ countdownFontFamily: brandFonts.globalBigFont });
                                        }}
                                    >
                                        ⭐ {brandFonts.globalBigFont}
                                    </button>
                                )}
                                {brandFonts?.globalSmallFont && brandFonts.globalSmallFont !== brandFonts.globalBigFont && (
                                    <button
                                        type="button"
                                        className={`cdb-font-family-option brand-font ${element.countdownFontFamily === brandFonts.globalSmallFont ? 'is-active' : ''}`}
                                        style={{ fontFamily: brandFonts.globalSmallFont }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ countdownFontFamily: brandFonts.globalSmallFont });
                                        }}
                                    >
                                        ⭐ {brandFonts.globalSmallFont}
                                    </button>
                                )}
                                
                                {/* System Fonts (first 5) */}
                                {titleFonts.slice(0, 5).map((font, index) => (
                                    <button
                                        key={`title-${index}`}
                                        type="button"
                                        className={`cdb-font-family-option ${element.countdownFontFamily === font ? 'is-active' : ''}`}
                                        style={{ fontFamily: font }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ countdownFontFamily: font });
                                        }}
                                    >
                                        {font}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        <span className="cdb-section-label">Countdown Style</span>
                    </div>
                )}

                {/* 4️⃣ TIMER CONTAINER SECTION */}
                {expandedSection === 'timerContainer' && (
                    <div 
                        className="countdown-bar-expanded-section"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Desktop/Mobile Toggle */}
                        <div className="cdb-device-row">
                            <span className="cdb-device-label">Desktop</span>
                            <div 
                                className={`cdb-toggle ${countdownDevice === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setCountdownDevice(countdownDevice === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="cdb-toggle-slider"></div>
                            </div>
                            <span className="cdb-device-label">Mobile</span>
                        </div>
                        
                        {/* Timer Position */}
                        <div className="cdb-position-group">
                            <span className="cdb-color-label">Timer Position</span>
                            <div className="cdb-position-buttons">
                                <button
                                    type="button"
                                    className={`cdb-position-btn ${(element.timerPosition || 'right') === 'left' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ timerPosition: 'left' });
                                    }}
                                    title="Timer Left"
                                >
                                    <i className="fas fa-align-left"></i>
                                    <span>Left</span>
                                </button>
                                <button
                                    type="button"
                                    className={`cdb-position-btn ${(element.timerPosition || 'right') === 'center' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ timerPosition: 'center' });
                                    }}
                                    title="Timer Center"
                                >
                                    <i className="fas fa-align-center"></i>
                                    <span>Center</span>
                                </button>
                                <button
                                    type="button"
                                    className={`cdb-position-btn ${(element.timerPosition || 'right') === 'right' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ timerPosition: 'right' });
                                    }}
                                    title="Timer Right"
                                >
                                    <i className="fas fa-align-right"></i>
                                    <span>Right</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Timer Layout */}
                        <div className="cdb-layout-group">
                            <span className="cdb-color-label">Timer Layout</span>
                            <div className="cdb-layout-buttons">
                                <button
                                    type="button"
                                    className={`cdb-layout-btn ${(element.timerLayout || 'horizontal') === 'horizontal' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ timerLayout: 'horizontal' });
                                    }}
                                    title="Horizontal"
                                >
                                    <i className="fas fa-grip-horizontal"></i>
                                    <span>Horizontal</span>
                                </button>
                                <button
                                    type="button"
                                    className={`cdb-layout-btn ${(element.timerLayout || 'horizontal') === 'stacked' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ timerLayout: 'stacked' });
                                    }}
                                    title="Stacked"
                                >
                                    <i className="fas fa-grip-vertical"></i>
                                    <span>Stacked</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Timer Gap */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Gap</span>
                            <input
                                type="range"
                                min="0"
                                max="40"
                                value={countdownDevice === 'desktop' 
                                    ? (element.timerGap ?? 12) 
                                    : (element.timerGapMobile ?? 12)}
                                onChange={(e) => {
                                    const newGap = parseInt(e.target.value);
                                    if (countdownDevice === 'desktop') {
                                        onStyleChange({ timerGap: newGap });
                                    } else {
                                        onStyleChange({ timerGapMobile: newGap });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {countdownDevice === 'desktop' 
                                    ? (element.timerGap ?? 12) 
                                    : (element.timerGapMobile ?? 12)}px
                            </span>
                        </div>
                        
                        {/* Timer Padding */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Padding</span>
                            <input
                                type="range"
                                min="0"
                                max="40"
                                value={countdownDevice === 'desktop' 
                                    ? (element.timerContainerPadding ?? 0) 
                                    : (element.timerContainerPaddingMobile ?? 0)}
                                onChange={(e) => {
                                    const newPadding = parseInt(e.target.value);
                                    if (countdownDevice === 'desktop') {
                                        onStyleChange({ timerContainerPadding: newPadding });
                                    } else {
                                        onStyleChange({ timerContainerPaddingMobile: newPadding });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {countdownDevice === 'desktop' 
                                    ? (element.timerContainerPadding ?? 0) 
                                    : (element.timerContainerPaddingMobile ?? 0)}px
                            </span>
                        </div>
                        
                        {/* Timer Border Radius */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Radius</span>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                value={countdownDevice === 'desktop' 
                                    ? (element.timerContainerBorderRadius ?? 8) 
                                    : (element.timerContainerBorderRadiusMobile ?? 8)}
                                onChange={(e) => {
                                    const newRadius = parseInt(e.target.value);
                                    if (countdownDevice === 'desktop') {
                                        onStyleChange({ timerContainerBorderRadius: newRadius });
                                    } else {
                                        onStyleChange({ timerContainerBorderRadiusMobile: newRadius });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {countdownDevice === 'desktop' 
                                    ? (element.timerContainerBorderRadius ?? 8) 
                                    : (element.timerContainerBorderRadiusMobile ?? 8)}px
                            </span>
                        </div>
                        
                        {/* Timer Background Color - Immer verfügbar */}
                        <div className="cdb-color-group">
                            <span className="cdb-color-label">Background</span>
                            <div className="cdb-brand-colors">
                                {Object.values(colors).map((brandColor, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className="cdb-brand-color"
                                        style={{ backgroundColor: brandColor }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ timerContainerBackgroundColor: brandColor });
                                        }}
                                        title={`Background ${index + 1}`}
                                    />
                                ))}
                                {/* Transparent Option */}
                                <button
                                    type="button"
                                    className="cdb-brand-color"
                                    style={{ 
                                        background: 'linear-gradient(135deg, #f0f0f0 25%, transparent 25%, transparent 75%, #f0f0f0 75%), linear-gradient(135deg, #f0f0f0 25%, white 25%, white 75%, #f0f0f0 75%)',
                                        backgroundSize: '8px 8px',
                                        backgroundPosition: '0 0, 4px 4px'
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ timerContainerBackgroundColor: 'transparent' });
                                    }}
                                    title="Transparent"
                                />
                            </div>
                        </div>
                        
                        {/* Timer Border */}
                        <div className="cdb-effect-group">
                            <div className="cdb-effect-header">
                                <label className="cdb-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={element.timerContainerBorderEnabled || false}
                                        onChange={(e) => onStyleChange({ timerContainerBorderEnabled: e.target.checked })}
                                        className="cdb-custom-toggle-input"
                                    />
                                    <span className="cdb-custom-toggle-slider"></span>
                                    <span className="cdb-custom-toggle-label">Border</span>
                                </label>
                            </div>
                            
                            {element.timerContainerBorderEnabled && (
                                <div className="cdb-effect-controls">
                                    <div className="cdb-slider-control">
                                        <span className="cdb-slider-label">Width</span>
                                        <input
                                            type="range"
                                            min="1"
                                            max="8"
                                            value={element.timerContainerBorderWidth ?? 2}
                                            onChange={(e) => onStyleChange({ timerContainerBorderWidth: parseInt(e.target.value) })}
                                            className="cdb-slider"
                                        />
                                        <span className="cdb-slider-value">{element.timerContainerBorderWidth ?? 2}px</span>
                                    </div>
                                    <div className="cdb-brand-colors cdb-brand-colors-small">
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className="cdb-brand-color cdb-brand-color-small"
                                                style={{ backgroundColor: brandColor }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ timerContainerBorderColor: brandColor });
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="cdb-effect-divider" />
                        
                        {/* Timer Shadow */}
                        <div className="cdb-effect-group">
                            <div className="cdb-effect-header">
                                <label className="cdb-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={element.timerContainerShadowEnabled || false}
                                        onChange={(e) => onStyleChange({ timerContainerShadowEnabled: e.target.checked })}
                                        className="cdb-custom-toggle-input"
                                    />
                                    <span className="cdb-custom-toggle-slider"></span>
                                    <span className="cdb-custom-toggle-label">Shadow</span>
                                </label>
                            </div>
                            
                            {element.timerContainerShadowEnabled && (
                                <div className="cdb-effect-controls">
                                    <div className="cdb-slider-control">
                                        <span className="cdb-slider-label">Blur</span>
                                        <input
                                            type="range"
                                            min="5"
                                            max="50"
                                            value={element.timerShadowIntensity ?? 10}
                                            onChange={(e) => onStyleChange({ timerShadowIntensity: parseInt(e.target.value) })}
                                            className="cdb-slider"
                                        />
                                        <span className="cdb-slider-value">{element.timerShadowIntensity ?? 10}px</span>
                                    </div>
                                    <div className="cdb-slider-control">
                                        <span className="cdb-slider-label">Distance</span>
                                        <input
                                            type="range"
                                            min="0"
                                            max="30"
                                            value={element.timerShadowDistance ?? 4}
                                            onChange={(e) => onStyleChange({ timerShadowDistance: parseInt(e.target.value) })}
                                            className="cdb-slider"
                                        />
                                        <span className="cdb-slider-value">{element.timerShadowDistance ?? 4}px</span>
                                    </div>
                                    <div className="cdb-slider-control">
                                        <span className="cdb-slider-label">Opacity</span>
                                        <input
                                            type="range"
                                            min="5"
                                            max="80"
                                            value={element.timerShadowOpacity ?? 15}
                                            onChange={(e) => onStyleChange({ timerShadowOpacity: parseInt(e.target.value) })}
                                            className="cdb-slider"
                                        />
                                        <span className="cdb-slider-value">{element.timerShadowOpacity ?? 15}%</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <span className="cdb-section-label">Timer Container</span>
                    </div>
                )}

                {/* 5️⃣ TIMER SETTINGS SECTION */}
                {expandedSection === 'timerSettings' && (
                    <div 
                        className="countdown-bar-expanded-section"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Countdown Mode */}
                        <div className="cdb-mode-group">
                            <span className="cdb-color-label">Countdown Mode</span>
                            <div className="cdb-mode-buttons">
                                <button
                                    type="button"
                                    className={`cdb-mode-btn ${(element.countdownMode || 'fixed') === 'fixed' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ countdownMode: 'fixed' });
                                    }}
                                >
                                    <i className="fas fa-calendar-day"></i>
                                    <span>Fixed Date</span>
                                </button>
                                <button
                                    type="button"
                                    className={`cdb-mode-btn ${(element.countdownMode || 'fixed') === 'monthly' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ countdownMode: 'monthly' });
                                    }}
                                >
                                    <i className="fas fa-sync-alt"></i>
                                    <span>Monthly Reset</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Target Date (nur bei Fixed Mode) */}
                        {(element.countdownMode || 'fixed') === 'fixed' && (
                            <div className="cdb-date-input-wrapper">
                                <label className="cdb-date-label">
                                    <i className="fas fa-calendar-alt"></i> Target Date
                                </label>
                                <input
                                    type="datetime-local"
                                    className="cdb-date-input"
                                    value={element.targetDate || ''}
                                    onChange={(e) => onStyleChange({ targetDate: e.target.value })}
                                />
                            </div>
                        )}
                        
                        {/* Label Display Mode */}
                        <div className="cdb-label-mode-group">
                            <span className="cdb-color-label">Label Display</span>
                            <div className="cdb-label-mode-buttons-grid">
                                <button
                                    type="button"
                                    className={`cdb-label-mode-btn ${(element.labelDisplayMode || 'short') === 'short' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ labelDisplayMode: 'short' });
                                    }}
                                    title="Short Labels (d, h, m, s)"
                                >
                                    <span className="label-example">05d 12h</span>
                                    <span>Short</span>
                                </button>
                                <button
                                    type="button"
                                    className={`cdb-label-mode-btn ${(element.labelDisplayMode || 'short') === 'long' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ labelDisplayMode: 'long' });
                                    }}
                                    title="Long Labels (Days, Hours)"
                                >
                                    <span className="label-example">Days</span>
                                    <span>Long</span>
                                </button>
                                <button
                                    type="button"
                                    className={`cdb-label-mode-btn ${(element.labelDisplayMode || 'short') === 'none' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ labelDisplayMode: 'none' });
                                    }}
                                    title="No Labels"
                                >
                                    <span className="label-example">05 12</span>
                                    <span>None</span>
                                </button>
                                <button
                                    type="button"
                                    className={`cdb-label-mode-btn cdb-label-mode-btn-custom ${(element.labelDisplayMode || 'short') === 'custom' ? 'active' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onStyleChange({ labelDisplayMode: 'custom' });
                                    }}
                                    title="Custom Labels"
                                >
                                    <i className="fas fa-edit"></i>
                                    <span>Custom</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* Custom Labels - nur wenn Custom Mode aktiv */}
                        {(element.labelDisplayMode || 'short') === 'custom' && (
                            <div className="cdb-custom-labels-section">
                                <div className="cdb-custom-label-input-group">
                                    <label className="cdb-custom-label-title">Days Label</label>
                                    <input
                                        type="text"
                                        className="cdb-custom-label-input"
                                        value={element.customDayLabel || 'd'}
                                        onChange={(e) => onStyleChange({ customDayLabel: e.target.value })}
                                        placeholder="d"
                                        maxLength={10}
                                    />
                                </div>
                                <div className="cdb-custom-label-input-group">
                                    <label className="cdb-custom-label-title">Hours Label</label>
                                    <input
                                        type="text"
                                        className="cdb-custom-label-input"
                                        value={element.customHourLabel || 'h'}
                                        onChange={(e) => onStyleChange({ customHourLabel: e.target.value })}
                                        placeholder="h"
                                        maxLength={10}
                                    />
                                </div>
                                <div className="cdb-custom-label-input-group">
                                    <label className="cdb-custom-label-title">Minutes Label</label>
                                    <input
                                        type="text"
                                        className="cdb-custom-label-input"
                                        value={element.customMinuteLabel || 'm'}
                                        onChange={(e) => onStyleChange({ customMinuteLabel: e.target.value })}
                                        placeholder="m"
                                        maxLength={10}
                                    />
                                </div>
                                <div className="cdb-custom-label-input-group">
                                    <label className="cdb-custom-label-title">Seconds Label</label>
                                    <input
                                        type="text"
                                        className="cdb-custom-label-input"
                                        value={element.customSecondLabel || 's'}
                                        onChange={(e) => onStyleChange({ customSecondLabel: e.target.value })}
                                        placeholder="s"
                                        maxLength={10}
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/* Display Format */}
                        <div className="cdb-display-format">
                            <span className="cdb-color-label">Show Units</span>
                            <div className="cdb-checkbox-group">
                                <label className="cdb-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={element.showDays !== false}
                                        onChange={(e) => onStyleChange({ showDays: e.target.checked })}
                                    />
                                    <span>Days</span>
                                </label>
                                <label className="cdb-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={element.showHours !== false}
                                        onChange={(e) => onStyleChange({ showHours: e.target.checked })}
                                    />
                                    <span>Hours</span>
                                </label>
                                <label className="cdb-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={element.showMinutes !== false}
                                        onChange={(e) => onStyleChange({ showMinutes: e.target.checked })}
                                    />
                                    <span>Minutes</span>
                                </label>
                                <label className="cdb-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={element.showSeconds !== false}
                                        onChange={(e) => onStyleChange({ showSeconds: e.target.checked })}
                                    />
                                    <span>Seconds</span>
                                </label>
                            </div>
                        </div>
                        
                        <span className="cdb-section-label">Timer Settings</span>
                    </div>
                )}

                {/* 5️⃣ SPACING SECTION */}
                {expandedSection === 'spacing' && (
                    <div 
                        className="countdown-bar-expanded-section"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Desktop/Mobile Toggle */}
                        <div className="cdb-device-row">
                            <span className="cdb-device-label">Desktop</span>
                            <div 
                                className={`cdb-toggle ${spacingDevice === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setSpacingDevice(spacingDevice === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="cdb-toggle-slider"></div>
                            </div>
                            <span className="cdb-device-label">Mobile</span>
                        </div>
                        
                        {/* Margins */}
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Top</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={spacingDevice === 'desktop' 
                                    ? (element.marginTop ?? 0) 
                                    : (element.marginTopMobile ?? 0)}
                                onChange={(e) => {
                                    const newMargin = parseInt(e.target.value);
                                    if (spacingDevice === 'desktop') {
                                        onStyleChange({ marginTop: newMargin });
                                    } else {
                                        onStyleChange({ marginTopMobile: newMargin });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {spacingDevice === 'desktop' 
                                    ? (element.marginTop ?? 0) 
                                    : (element.marginTopMobile ?? 0)}px
                            </span>
                        </div>
                        
                        <div className="cdb-slider-control">
                            <span className="cdb-slider-label">Bottom</span>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={spacingDevice === 'desktop' 
                                    ? (element.marginBottom ?? 0) 
                                    : (element.marginBottomMobile ?? 0)}
                                onChange={(e) => {
                                    const newMargin = parseInt(e.target.value);
                                    if (spacingDevice === 'desktop') {
                                        onStyleChange({ marginBottom: newMargin });
                                    } else {
                                        onStyleChange({ marginBottomMobile: newMargin });
                                    }
                                }}
                                className="cdb-slider"
                            />
                            <span className="cdb-slider-value">
                                {spacingDevice === 'desktop' 
                                    ? (element.marginBottom ?? 0) 
                                    : (element.marginBottomMobile ?? 0)}px
                            </span>
                        </div>
                        
                        <span className="cdb-section-label">Spacing</span>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default CountdownBarFloatingToolbar;

